import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ネットワークエラーの種類
 */
export type NetworkErrorType =
  | 'offline' // オフライン状態
  | 'timeout' // タイムアウト
  | 'cancelled' // ユーザーによるキャンセル（AbortController）
  | 'connection_failed' // 接続失敗（DNS、サーバー到達不可等）
  | 'server_config_error' // サーバー設定エラー（CORS、SSL証明書等）
  | null; // ネットワークエラーではない

/**
 * ブラウザ標準のネットワークエラーメッセージパターン
 * 各ブラウザで異なるメッセージが返される
 */
const NETWORK_ERROR_PATTERNS = {
  // 接続失敗（fetch API標準）
  connectionFailed: [
    'Failed to fetch', // Chrome, Firefox
    'NetworkError', // Firefox (一部ケース)
    'Network request failed', // React Native等
    'ERR_NETWORK', // Axios等
    'ERR_CONNECTION_REFUSED',
    'ERR_NAME_NOT_RESOLVED',
    'ENOTFOUND',
    'ECONNREFUSED',
    'ECONNRESET',
  ],
  // Safari特有のエラー
  safari: [/^load failed/i, /^The network connection was lost/i],
  // タイムアウト
  timeout: [
    /timeout/i,
    /timed out/i,
    /ETIMEDOUT/,
    /ERR_TIMED_OUT/,
    /deadline exceeded/i,
  ],
  // CORS/SSL関連（サーバー設定の問題）
  serverConfig: [
    /CORS/i,
    /cross-origin/i,
    /SSL/i,
    /certificate/i,
    /ERR_CERT_/,
    /ERR_SSL_/,
    /SEC_ERROR_/,
    /MOZILLA_PKIX_ERROR_/,
  ],
} as const;

/**
 * エラーメッセージがパターンに一致するか判定する
 */
function matchesPattern(
  message: string,
  patterns: readonly (string | RegExp)[],
): boolean {
  return patterns.some((pattern) => {
    if (typeof pattern === 'string') {
      return message === pattern || message.includes(pattern);
    }
    return pattern.test(message);
  });
}

/**
 * フェッチエラーからネットワークエラーの種類を判別する
 * @param error - エラーメッセージまたはErrorオブジェクト
 * @returns ネットワークエラーの種類、またはnull
 */
export function detectNetworkErrorType(
  error: string | Error | unknown,
): NetworkErrorType {
  // オフライン状態の検出
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'offline';
  }

  // Errorオブジェクトの場合、nameプロパティも確認
  if (error instanceof Error) {
    // AbortError: ユーザーによる明示的なキャンセル
    if (error.name === 'AbortError') {
      return 'cancelled';
    }
    // TimeoutError: 明示的なタイムアウト
    if (error.name === 'TimeoutError') {
      return 'timeout';
    }
    // TypeError: fetchのネットワークエラー（Chrome/Firefox）
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      return 'connection_failed';
    }
  }

  const errorMessage =
    error instanceof Error ? error.message : String(error ?? '');

  // 空のメッセージは無視
  if (!errorMessage) {
    return null;
  }

  // サーバー設定エラー（CORS/SSL）を先にチェック
  // これらはユーザーの接続問題ではなくサーバー側の設定問題
  if (matchesPattern(errorMessage, NETWORK_ERROR_PATTERNS.serverConfig)) {
    return 'server_config_error';
  }

  // タイムアウトエラーの検出
  if (matchesPattern(errorMessage, NETWORK_ERROR_PATTERNS.timeout)) {
    return 'timeout';
  }

  // 接続失敗エラーの検出（ブラウザ標準のメッセージ）
  if (matchesPattern(errorMessage, NETWORK_ERROR_PATTERNS.connectionFailed)) {
    return 'connection_failed';
  }

  // Safari特有のエラーパターン
  if (matchesPattern(errorMessage, NETWORK_ERROR_PATTERNS.safari)) {
    return 'connection_failed';
  }

  return null;
}

/**
 * ネットワークエラーの種類に対応する翻訳キーを取得する
 * @param errorType - ネットワークエラーの種類
 * @returns 翻訳キー
 */
export function getNetworkErrorTranslationKey(
  errorType: NetworkErrorType,
): string {
  switch (errorType) {
    case 'offline':
      return 'network_offline';
    case 'timeout':
      return 'network_timeout';
    case 'cancelled':
      return 'network_cancelled';
    case 'connection_failed':
      return 'network_connection_failed';
    case 'server_config_error':
      return 'network_server_config_error';
    default:
      return 'network_error';
  }
}

interface ApiErrorResult {
  message: string;
  errorKey: string;
  detailMessages: string[];
  isFullyBooked: boolean;
  isNetworkError: boolean;
  networkErrorType: NetworkErrorType;
}

/**
 * APIエラーメッセージを翻訳するためのカスタムフック
 *
 * 使用例:
 * const { translateApiError } = useApiErrorTranslation();
 * const { message, detailMessages, isFullyBooked } = translateApiError(result.error, result.details);
 */
export const useApiErrorTranslation = () => {
  const { t } = useTranslation();

  /**
   * エラーキーを翻訳する（errors.api.プレフィックスを優先、見つからなければ直接キーを試す）
   */
  const translateKey = useCallback(
    (key: string): string => {
      const apiKey = `errors.api.${key}`;
      const apiTranslated = t(apiKey);
      if (apiTranslated !== apiKey) {
        return apiTranslated;
      }
      const directTranslated = t(key);
      return directTranslated !== key ? directTranslated : key;
    },
    [t],
  );

  /**
   * APIエラーを翻訳する
   * @param error - エラーキー（例: auth.authentication_failed）
   * @param details - エラーの詳細情報（オプショナル）
   * @returns 翻訳されたメッセージと状態フラグ
   */
  const translateApiError = useCallback(
    (error: string | undefined, details?: string[]): ApiErrorResult => {
      // errorがundefinedまたは空の場合
      if (!error) {
        return {
          message: '',
          errorKey: '',
          detailMessages: [],
          isFullyBooked: false,
          isNetworkError: false,
          networkErrorType: null,
        };
      }

      // ネットワークエラーの種類を検出
      const networkErrorType = detectNetworkErrorType(error);
      const isNetworkError = networkErrorType !== null;

      // ネットワークエラーの場合は専用の翻訳キーを使用
      if (isNetworkError) {
        const translationKey = getNetworkErrorTranslationKey(networkErrorType);
        return {
          message: t(`errors.api.${translationKey}`),
          errorKey: translationKey,
          detailMessages: [],
          isFullyBooked: false,
          isNetworkError: true,
          networkErrorType,
        };
      }

      // 特定のエラーキー検出
      const isFullyBooked = error === 'reservation.create.fully_booked';

      return {
        message: translateKey(error),
        errorKey: error,
        detailMessages: details ? details.map(translateKey) : [],
        isFullyBooked,
        isNetworkError: false,
        networkErrorType: null,
      };
    },
    [t, translateKey],
  );

  return { translateApiError };
};
